/**
 * A chart text (title, axis title, user-shape text box) retyped as plain
 * text, laid back onto its runs so their formatting stays: the common
 * prefix and suffix keep their runs, the typed middle takes the run it
 * starts in (the run before the caret), and a typed newline starts a
 * paragraph that copies that paragraph's properties.
 *
 * The core works on run texts only and is mirrored character for character
 * by App\Services\Docx\Studio\Compile\Charts\ChartTextRuns (the compiler
 * writes the a:p/a:r XML the canvas previews); both run
 * tests/Fixtures/charts/text-edit-vectors.json. Characters are Unicode
 * code points on both sides.
 */

const SEPARATOR = '\n'

/** Plain text of paragraphs of runs ([[runText…]…]): paragraphs joined by newlines. */
export function plainText(paragraphs) {
  return paragraphs.map((runs) => runs.join('')).join(SEPARATOR)
}

/**
 * @param {string[][]} paragraphs run texts per paragraph
 * @param {string} text the new plain text
 * @returns {{ p: number, runs: { r: number|null, p: number, text: string }[] }[]}
 *   each output paragraph: its source paragraph; each run: its source run
 *   (paragraph p, run r; r null = no run to copy, the paragraph's own properties)
 */
export function layTextOnRuns(paragraphs, text) {
  const old = []
  paragraphs.forEach((runs, p) => {
    if (p > 0) old.push({ ch: SEPARATOR, sep: true, p })
    runs.forEach((run, r) => {
      for (const ch of Array.from(run)) old.push({ ch, p, r })
    })
  })
  const typed = Array.from(text)
  const limit = Math.min(old.length, typed.length)
  let prefix = 0
  while (prefix < limit && old[prefix].ch === typed[prefix]) prefix++
  let suffix = 0
  while (suffix < limit - prefix && old[old.length - 1 - suffix].ch === typed[typed.length - 1 - suffix]) suffix++

  // The paragraph the caret is in at the start of the change, and the run
  // the typed text takes: the character before it, else the one after it,
  // inside that paragraph.
  let paragraph = 0
  for (let index = 0; index < prefix; index++) if (old[index].sep) paragraph = old[index].p
  let anchor = null
  if (prefix > 0 && !old[prefix - 1].sep) anchor = old[prefix - 1]
  else if (prefix < old.length && !old[prefix].sep) anchor = old[prefix]
  const source = { p: anchor ? anchor.p : paragraph, r: anchor ? anchor.r : null }

  const inserted = typed.slice(prefix, typed.length - suffix).map((ch) => (ch === SEPARATOR ? { ch, sep: true, p: source.p } : { ch, p: source.p, r: source.r }))
  const result = [...old.slice(0, prefix), ...inserted, ...old.slice(old.length - suffix)]

  const out = []
  let current = { p: paragraphs.length ? 0 : 0, runs: [], opened: null }
  const flush = () => {
    const first = current.runs[0]
    out.push({ p: first ? first.p : (current.opened ?? 0), runs: current.runs })
  }
  for (const item of result) {
    if (item.sep) {
      flush()
      current = { runs: [], opened: item.p }
      continue
    }
    const last = current.runs[current.runs.length - 1]
    if (last && last.p === item.p && last.r === item.r) last.text += item.ch
    else current.runs.push({ p: item.p, r: item.r, text: item.ch })
  }
  flush()
  return out
}

/**
 * The model paragraphs ([{ align, runs: [{ text, style }], style }]) of a
 * chart text after retyping it as `text`: the preview of what the compiler
 * writes.
 */
export function retypeParagraphs(paragraphs, text) {
  const laid = layTextOnRuns(
    paragraphs.map((paragraph) => (paragraph.runs ?? []).map((run) => String(run.text))),
    text,
  )
  return laid.map((line) => {
    const source = paragraphs[line.p] ?? paragraphs[0] ?? { align: null, style: null, runs: [] }
    return {
      align: source.align ?? null,
      style: source.style ?? null,
      runs: line.runs.map((run) => ({ text: run.text, style: (run.r === null ? null : paragraphs[run.p]?.runs?.[run.r]?.style) ?? source.style })),
    }
  })
}

/** The plain text of model paragraphs. */
export function paragraphsText(paragraphs) {
  return plainText((paragraphs ?? []).map((paragraph) => (paragraph.runs ?? []).map((run) => String(run.text))))
}
